import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsTrack } from '@/features/amplitude'
import { useCompleteOnboardingStepIfNeeded } from '@/features/onboarding/user-onboarding-steps/onboarding.hooks'
import { selectOnboardingStepCompletedOrSkipped } from '@/features/onboarding/user-onboarding-steps/onboarding.selectors'
import { getNeededBankStatementsForCatchupBookkeeping } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { markUserActionItemCompleteIfExists } from '../../lib/service'
import { ScheduleOnboardingCallStatus } from '../../lib/userActionItemStatuses'
import { GettingStartedOnboardingStep } from '@/features/onboarding/user-onboarding-steps/onboarding.reducer'
import { fetchUserOnboardingStepsIfNeeded } from '@/features/onboarding/user-onboarding-steps/onboarding.actions'
import { OnboardingTaskIdents } from '@/features/onboarding/config'

import { Colors } from '@/src/styles/theme'
import { Modal } from '@/base'
import { ViewMissingStatementsAccordion } from '@/components/ViewMissingStatementsAccordion'

import { useEffect } from 'react'

// TODO: Migrate to V2
import { useFinancialAccountsAutomaticStatementsCard } from '@/componentsV1/Finances/Accounts/automaticStatements.hooks'

/**
 * Records that the user was sent to Connect & Clarify. Registration happens on
 * Calendly's own page, so the click is the only signal the app gets — and these
 * records are what release the user from the onboarding dashboard.
 */
export const useMarkConnectAndClarifyRegistered = () => {
  const track = useAnalyticsTrack()
  const onboardingStepCompletion = useCompleteOnboardingStepIfNeeded(
    GettingStartedOnboardingStep.joinGroupOnboardingSession
  )
  const stepAlreadyResolved = useReselector(
    selectOnboardingStepCompletedOrSkipped,
    GettingStartedOnboardingStep.joinGroupOnboardingSession
  )

  return async () => {
    await markUserActionItemCompleteIfExists(
      OnboardingTaskIdents.SCHEDULE_ONBOARDING_MEETING,
      track,
      ScheduleOnboardingCallStatus.scheduled
    )
    // Someone booking a repeat session has already finished this step. Firing
    // the completion event again would inflate a first-time sign-up metric.
    if (stepAlreadyResolved) return
    await onboardingStepCompletion.markComplete()
    track('completed onboarding item schedule call', {
      status: 'done',
    })
  }
}

export const AutomaticStatementUploadModal = ({
  open = false,
  setOpen,
}: {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const automaticStatementUploadCard =
    useFinancialAccountsAutomaticStatementsCard()
  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <Modal.Content style={{ backgroundColor: Colors.natural }}>
        {automaticStatementUploadCard.financialAccountAlert}
      </Modal.Content>
    </Modal>
  )
}

export const ViewMissingStatementsAccordionForCatchupBookkeeping = () => {
  const dispatch = useAppDispatch()
  const missingStatements = useFetchResponse(
    getNeededBankStatementsForCatchupBookkeeping
  )
  useEffect(() => {
    if (Array.isArray(missingStatements) && missingStatements.length > 0) {
      dispatch(
        fetchUserOnboardingStepsIfNeeded({ expanded: true, alwaysFetch: true })
      )
    }
  }, [missingStatements, dispatch])
  if (!missingStatements || missingStatements.length === 0) {
    return null
  }
  return (
    <ViewMissingStatementsAccordion
      neededStatements={missingStatements}
      title={'View missing statements'}
    />
  )
}
